// Global interactive component effects, initialized once:
//  - 3D tilt + cursor-following glow on cards (event delegation, works on
//    every page and everything rendered later — no per-component wiring)
//  - scroll-reveal (IntersectionObserver + MutationObserver)
// Everything is skipped for touch-only devices / reduced motion.

const TILT_SELECTOR = [
    '.bp-product-card', '.jp-how-card', '.bp-join-card', '.bp-plan-card',
    '.bp-result-card', '.bp-cart-item', '.bp-order-card', '.bp-auth-card',
    '.homepage-seller-details .card', '.bp-phone-mock',
].join(',');

const REVEAL_SELECTOR = [
    '.bp-product-card', '.jp-how-card', '.bp-join-card', '.bp-plan-card',
    '.bp-result-card', '.suggested-iteams', '.bp-order-card',
    '.homepage-seller-details .card', '.review_card', '.bp-footer-col',
].join(',');

const MAX_TILT = 7; // degrees

export default function initInteractiveFX() {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    document.documentElement.classList.add('fx');

    /* ---- tilt + glow ---- */
    if (!reduced && finePointer) {
        let current = null;

        const onMove = (e) => {
            const card = e.target.closest ? e.target.closest(TILT_SELECTOR) : null;
            if (card !== current) {
                if (current) resetCard(current);
                current = card;
                if (current) current.classList.add('fx-tilt');
            }
            if (!current) return;
            const rect = current.getBoundingClientRect();
            if (!rect.width) return;
            const px = (e.clientX - rect.left) / rect.width;   // 0..1
            const py = (e.clientY - rect.top) / rect.height;
            const rx = (0.5 - py) * MAX_TILT;
            const ry = (px - 0.5) * MAX_TILT;
            current.style.setProperty('--rx', rx.toFixed(2) + 'deg');
            current.style.setProperty('--ry', ry.toFixed(2) + 'deg');
            current.style.setProperty('--gx', (px * 100).toFixed(1) + '%');
            current.style.setProperty('--gy', (py * 100).toFixed(1) + '%');
        };

        const resetCard = (card) => {
            card.classList.remove('fx-tilt');
            card.style.removeProperty('--rx');
            card.style.removeProperty('--ry');
        };

        const onOut = (e) => {
            if (current && (!e.relatedTarget || !current.contains(e.relatedTarget))) {
                const card = e.target.closest ? e.target.closest(TILT_SELECTOR) : null;
                if (card === current) { resetCard(current); current = null; }
            }
        };

        document.addEventListener('pointermove', onMove, { passive: true });
        document.addEventListener('pointerout', onOut, { passive: true });
    }

    /* ---- scroll reveal ---- */
    if (!reduced && 'IntersectionObserver' in window) {
        const seen = new WeakSet();
        const io = new IntersectionObserver((entries) => {
            for (const en of entries) {
                if (en.isIntersecting) {
                    en.target.classList.add('fx-in');
                    io.unobserve(en.target);
                }
            }
        }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

        const scan = () => {
            document.querySelectorAll(REVEAL_SELECTOR).forEach((el) => {
                if (seen.has(el)) return;
                seen.add(el);
                // already on screen? show instantly (no first-paint flicker)
                const r = el.getBoundingClientRect();
                if (r.top < window.innerHeight * 0.92 && r.bottom > 0) {
                    el.classList.add('fx-in');
                } else {
                    el.classList.add('fx-reveal');
                    io.observe(el);
                }
            });
        };

        scan();
        const mo = new MutationObserver(() => {
            clearTimeout(mo._t);
            mo._t = setTimeout(scan, 80);
        });
        mo.observe(document.body, { childList: true, subtree: true });
    }
}
